import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, CheckCircle, CheckCircle2, CreditCard, Calendar, User, BookOpen, Award, Mail, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EnrollmentFlow = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(1);
  const [showSuccessFlow, setShowSuccessFlow] = useState(false);
  const [courseSchedule, setCourseSchedule] = useState([]);
  const [loading, setLoading] = useState(false);
  const [studentData, setStudentData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    referral_code: ''
  });
  const [courseSelection, setCourseSelection] = useState({
    course_type: '',
    course_start_date: ''
  });
  const [paymentType, setPaymentType] = useState('enrollment'); // 'enrollment', 'course', 'combined'
  const [studentId, setStudentId] = useState(null);
  const [showVerificationMessage, setShowVerificationMessage] = useState(false);
  // Captcha state
  const [captchaQuestion, setCaptchaQuestion] = useState({ num1: 6, num2: 2, answer: 8 });
  const [captchaInput, setCaptchaInput] = useState('');
  const [captchaValid, setCaptchaValid] = useState(false);

  useEffect(() => {
    fetchCourseSchedule();
    generateCaptcha(); // Generate initial captcha
    
    // Check if this is a success redirect after payment
    const success = searchParams.get('success');
    if (success === 'true') {
      setShowSuccessFlow(true);
    }
  }, [searchParams]);

  const fetchCourseSchedule = async () => {
    try {
      const response = await axios.get(`${API}/course-dates`);
      setCourseSchedule(response.data.schedule);
    } catch (error) {
      console.error('Error fetching course schedule:', error);
      toast.error('Failed to load course dates');
    }
  };

  const handleStudentDataChange = (field, value) => {
    setStudentData(prev => ({ ...prev, [field]: value }));
  };

  const handleCourseSelectionChange = (field, value) => {
    setCourseSelection(prev => ({ ...prev, [field]: value }));
  };

  const generateCaptcha = () => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    setCaptchaQuestion({ num1, num2, answer: num1 + num2 });
    setCaptchaInput('');
    setCaptchaValid(false);
  };

  const validateCaptcha = (input) => {
    const isValid = parseInt(input) === captchaQuestion.answer;
    setCaptchaValid(isValid);
    return isValid;
  };

  const createStudent = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/students`, studentData);
      setStudentId(response.data.id);
      
      // Trigger live notification
      if (window.addSignupNotification) {
        window.addSignupNotification({
          ...studentData,
          course_type: courseSelection.course_type
        });
      }
      
      toast.success('Registration created successfully!');
      return response.data.id;
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Email already registered. Please use a different email.');
      } else {
        toast.error('Failed to create registration');
      }
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateCourseSelection = async (id) => {
    try {
      await axios.post(`${API}/students/${id}/course-selection`, {
        student_id: id,
        ...courseSelection
      });
      toast.success('Course selection updated!');
    } catch (error) {
      toast.error('Failed to update course selection');
      throw error;
    }
  };

  const calculateTotal = () => {
    let total = 0;
    if (paymentType === 'enrollment' || paymentType === 'combined') {
      total += 9.99;
    }
    if (paymentType === 'course' || paymentType === 'combined') {
      total += courseSelection.course_type === 'foundation' ? 75.00 : 215.00;
    }
    return total.toFixed(2);
  };

  const nextStep = async () => {
    try {
      if (currentStep === 1) {
        // Validate student data
        if (!studentData.first_name || !studentData.last_name || !studentData.email || !studentData.password) {
          toast.error('Please fill in all required fields');
          return;
        }
        
        // Validate password length
        if (studentData.password.length < 8) {
          toast.error('Password must be at least 8 characters long');
          return;
        }
        
        // Validate captcha
        if (!captchaValid) {
          toast.error('Please complete the verification calculation correctly');
          return;
        }
        
        const id = await createStudent();
        if (id) {
          setStudentId(id);
          toast.success('Account created! Check your email for next steps.');
          setCurrentStep(2);
        }
      } else if (currentStep === 2) {
        // Move to course selection
        setCurrentStep(3);
      } else if (currentStep === 3) {
        // Validate course selection if needed
        if (paymentType === 'course' || paymentType === 'combined') {
          if (!courseSelection.course_type || !courseSelection.course_start_date) {
            toast.error('Please select course type and start date');
            return;
          }
          await updateCourseSelection(studentId);
        }
        setCurrentStep(4);
      }
    } catch (error) {
      // Error already handled in individual functions
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getSelectedCourseInfo = () => {
    const selected = courseSchedule.find(course => course.start === courseSelection.course_start_date);
    return selected || null;
  };

  const renderStepIndicator = () => {
    const steps = [
      { number: 1, title: 'Personal Info', icon: User },
      { number: 2, title: 'Payment Type', icon: CreditCard },
      { number: 3, title: 'Course Selection', icon: BookOpen },
      { number: 4, title: 'Payment', icon: CreditCard },
      { number: 5, title: 'Confirmation', icon: Award }
    ];

    return (
      <div className="step-indicator mb-8">
        {steps.map((step, index) => {
          const Icon = step.icon;
          return (
            <div key={step.number} className="flex items-center">
              <div className={`step ${currentStep >= step.number ? 'active' : ''} ${currentStep > step.number ? 'completed' : ''}`}>
                <div className="step-number">
                  {currentStep > step.number ? <CheckCircle2 className="w-4 h-4" /> : step.number}
                </div>
                <span className="ml-2 hidden sm:inline">{step.title}</span>
              </div>
              {index < steps.length - 1 && (
                <div className="w-8 h-0.5 bg-slate-300 mx-2" />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderStep1 = () => (
    <Card className="card max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <User className="w-12 h-12 text-blue-600 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-slate-800">Personal Information</h2>
        <p className="text-slate-600 mt-2">Let's get started with your enrollment</p>
      </div>

      <div className="space-y-6">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="firstName">First Name *</Label>
            <Input
              id="firstName"
              value={studentData.first_name}
              onChange={(e) => handleStudentDataChange('first_name', e.target.value)}
              placeholder="Enter your first name"
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="lastName">Last Name *</Label>
            <Input
              id="lastName"
              value={studentData.last_name}
              onChange={(e) => handleStudentDataChange('last_name', e.target.value)}
              placeholder="Enter your last name"
              className="mt-1"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="email">Email Address *</Label>
          <Input
            id="email"
            type="email"
            value={studentData.email}
            onChange={(e) => handleStudentDataChange('email', e.target.value)}
            placeholder="Enter your email address"
            className="mt-1"
          />
        </div>

        <div>
          <Label htmlFor="phone">Phone Number</Label>
          <Input
            id="phone"
            type="tel"
            value={studentData.phone}
            onChange={(e) => handleStudentDataChange('phone', e.target.value)}
            placeholder="Enter your phone number (optional)"
            className="mt-1"
          />
        </div>

        <div>
          <Label htmlFor="password">Create Password *</Label>
          <Input
            id="password"
            type="password"
            value={studentData.password}
            onChange={(e) => handleStudentDataChange('password', e.target.value)}
            placeholder="Create a strong password"
            className="mt-1"
          />
          <p className="text-xs text-slate-500 mt-1">Minimum 8 characters recommended</p>
        </div>

        <div>
          <Label htmlFor="referral">Referral Code</Label>
          <Input
            id="referral"
            value={studentData.referral_code}
            onChange={(e) => handleStudentDataChange('referral_code', e.target.value)}
            placeholder="Enter referral code if you have one"
            className="mt-1"
          />
        </div>

        {/* Simple Captcha */}
        <div className="mt-6 p-4 border-2 border-blue-200 rounded-lg bg-blue-50">
          <Label className="block text-sm font-semibold text-blue-800 mb-2">
            Security Verification
          </Label>
          <div className="flex items-center space-x-3">
            <span className="text-lg font-semibold text-slate-800">
              {captchaQuestion.num1} + {captchaQuestion.num2} = ?
            </span>
            <Input
              type="number"
              value={captchaInput}
              onChange={(e) => {
                setCaptchaInput(e.target.value);
                validateCaptcha(e.target.value);
              }}
              placeholder="Answer"
              className={`w-20 ${captchaValid ? 'border-green-500 bg-green-50' : 'border-slate-300'}`}
            />
            {captchaValid && (
              <CheckCircle className="w-5 h-5 text-green-600" />
            )}
            <Button 
              type="button" 
              size="sm" 
              variant="outline"
              onClick={generateCaptcha}
              className="text-xs"
            >
              New
            </Button>
          </div>
          <p className="text-xs text-blue-600 mt-1">
            Complete this simple calculation to proceed
          </p>
        </div>
      </div>

      <div className="flex justify-between mt-8">
        <Link to="/" className="btn btn-secondary">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
        </Link>
        <Button onClick={nextStep} disabled={loading} className="btn btn-primary">
          {loading ? 'Creating...' : 'Continue'} <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </Card>
  );

  const renderStep2 = () => (
    <Card className="card max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <CreditCard className="w-12 h-12 text-blue-600 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-slate-800">Choose Your Payment Option</h2>
        <p className="text-slate-600 mt-2">Select how you'd like to proceed</p>
      </div>

      <div className="space-y-4">
        <Card 
          className={`p-4 cursor-pointer border-2 transition-all ${paymentType === 'enrollment' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`}
          onClick={() => setPaymentType('enrollment')}
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold">Enrollment Fee Only</h3>
              <p className="text-sm text-slate-600">Pay £9.99 now, select course later</p>
            </div>
            <div className="text-xl font-bold text-blue-600">£9.99</div>
          </div>
        </Card>

        <Card 
          className={`p-4 cursor-pointer border-2 transition-all ${paymentType === 'combined' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`}
          onClick={() => setPaymentType('combined')}
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold">Complete Package</h3>
              <p className="text-sm text-slate-600">Enrollment + Course in one payment</p>
              <Badge className="mt-1 bg-green-100 text-green-800">Recommended</Badge>
            </div>
            <div className="text-xl font-bold text-green-600">Save Time</div>
          </div>
        </Card>
      </div>

      <div className="flex justify-between mt-8">
        <Button onClick={prevStep} variant="outline">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>
        <Button onClick={nextStep} className="btn btn-primary">
          Continue <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </Card>
  );

  const renderStep3 = () => (
    <Card className="card max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <BookOpen className="w-12 h-12 text-blue-600 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-slate-800">Course Selection</h2>
        <p className="text-slate-600 mt-2">
          {paymentType === 'enrollment' ? 'You can complete this later after enrollment' : 'Choose your course and start date'}
        </p>
      </div>

      {paymentType !== 'enrollment' && (
        <div className="space-y-6">
          {/* Course Type Selection */}
          <div>
            <Label className="text-lg font-semibold mb-4 block">Select Course Type</Label>
            <div className="grid md:grid-cols-2 gap-6">
              <Card 
                className={`p-6 cursor-pointer border-2 transition-all ${courseSelection.course_type === 'foundation' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`}
                onClick={() => handleCourseSelectionChange('course_type', 'foundation')}
              >
                <h3 className="text-xl font-semibold mb-2">Foundation Course</h3>
                <div className="text-2xl font-bold text-blue-600 mb-2">£75.00</div>
                <p className="text-slate-600 mb-4">6 comprehensive modules covering all essentials</p>
                <ul className="text-sm space-y-1">
                  <li>✓ AI Review Creation</li>
                  <li>✓ Video Production</li>
                  <li>✓ Monetization Strategies</li>
                  <li>✓ Professional Certification</li>
                </ul>
              </Card>

              <Card 
                className={`p-6 cursor-pointer border-2 transition-all ${courseSelection.course_type === 'pro' ? 'border-purple-500 bg-purple-50' : 'border-slate-200 hover:border-slate-300'}`}
                onClick={() => handleCourseSelectionChange('course_type', 'pro')}
              >
                <Badge className="mb-2 bg-purple-100 text-purple-800">Most Popular</Badge>
                <h3 className="text-xl font-semibold mb-2">Pro Course</h3>
                <div className="text-2xl font-bold text-purple-600 mb-2">£215.00</div>
                <p className="text-slate-600 mb-4">Foundation + 5 advanced business modules</p>
                <ul className="text-sm space-y-1">
                  <li>✓ Everything in Foundation</li>
                  <li>✓ 5 advanced modules</li>
                  <li>✓ Brand building masterclass</li>
                  <li>✓ Client Chatbot Training funnel creation</li>
                  <li>✓ Landing Page and ecommerce website</li>
                  <li>✓ Automated client systems</li>
                  <li>✓ Agency scaling strategies</li>
                  <li>✓ 6 video testimonials</li>
                </ul>
              </Card>
            </div>

            {/* ID Verification Warning */}
            {courseSelection.course_type && (
              <Card className="mt-6 p-4 bg-amber-50 border-amber-200">
                <div className="flex items-start">
                  <User className="w-5 h-5 text-amber-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-amber-800">ID Verification Required</h4>
                    <p className="text-sm text-amber-700 mt-1">
                      Before accessing any course materials, you will need to upload a valid photo ID (driving license, passport, or government-issued ID card). 
                      This ensures the integrity of our certification process. Please have your ID ready for when your course begins.
                    </p>
                  </div>
                </div>
              </Card>
            )}
          </div>

          {/* Course Start Date Selection */}
          {courseSelection.course_type && (
            <div>
              <Label className="text-lg font-semibold mb-4 block">Select Start Date</Label>
              <Select onValueChange={(value) => handleCourseSelectionChange('course_start_date', value)}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Choose your preferred start date" />
                </SelectTrigger>
                <SelectContent>
                  {courseSchedule.map((course, index) => (
                    <SelectItem key={index} value={course.start}>
                      <div className="flex items-center justify-between w-full">
                        <span>{course.enrol}</span>
                        <span className="ml-4 text-slate-600">{formatDate(course.start)}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {courseSelection.course_start_date && (
                <Card className="mt-4 p-4 bg-green-50 border-green-200">
                  <h4 className="font-semibold text-green-800 mb-2">Selected Course Schedule:</h4>
                  {(() => {
                    const selected = getSelectedCourseInfo();
                    return selected ? (
                      <div className="text-sm space-y-1">
                        <p><strong>Start:</strong> {formatDate(selected.start)}</p>
                        <p><strong>End:</strong> {formatDate(selected.end)}</p>
                        <p><strong>Graduation:</strong> {formatDate(selected.graduation)}</p>
                      </div>
                    ) : null;
                  })()}
                </Card>
              )}
            </div>
          )}
        </div>
      )}

      {paymentType === 'enrollment' && (
        <Card className="p-6 bg-blue-50 border-blue-200">
          <p className="text-blue-800">
            After completing your enrollment payment, you can return to select your course type and start date. 
            This gives you time to review the materials and make an informed decision.
          </p>
        </Card>
      )}

      <div className="flex justify-between mt-8">
        <Button onClick={prevStep} variant="outline">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>
        <Button onClick={nextStep} className="btn btn-primary">
          Proceed to Payment <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </Card>
  );

  const renderStep4 = () => (
    <Card className="card max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <CreditCard className="w-12 h-12 text-blue-600 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-slate-800">Complete Payment</h2>
        <p className="text-slate-600 mt-2">Secure payment processing with Square</p>
      </div>

      {/* Order Summary */}
      <Card className="p-6 bg-slate-50 mb-6">
        <h3 className="font-semibold mb-4">Order Summary</h3>
        <div className="space-y-2">
          {(paymentType === 'enrollment' || paymentType === 'combined') && (
            <div className="flex justify-between">
              <span>Enrollment Fee</span>
              <span>£9.99</span>
            </div>
          )}
          {(paymentType === 'course' || paymentType === 'combined') && courseSelection.course_type && (
            <div className="flex justify-between">
              <span>{courseSelection.course_type === 'foundation' ? 'Foundation' : 'Pro'} Course</span>
              <span>£{courseSelection.course_type === 'foundation' ? '75.00' : '215.00'}</span>
            </div>
          )}
          <hr className="my-2" />
          <div className="flex justify-between font-bold text-lg">
            <span>Total</span>
            <span>£{calculateTotal()}</span>
          </div>
        </div>

        {courseSelection.course_start_date && (
          <div className="mt-4 pt-4 border-t">
            <p className="text-sm text-slate-600">
              <strong>Course Start:</strong> {formatDate(courseSelection.course_start_date)}
            </p>
          </div>
        )}
      </Card>

      {/* Revolut Payment Options */}
      <div className="payment-options-container">
        <h3 className="font-semibold mb-4 flex items-center">
          <CreditCard className="w-5 h-5 mr-2" />
          Complete Your Payment
        </h3>
        
        <div className="space-y-4">
          {/* Enrollment Only */}
          {paymentType === 'enrollment' && (
            <div className="bg-white border-2 border-blue-200 rounded-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h4 className="font-semibold text-lg">Enrollment Fee</h4>
                  <p className="text-2xl font-bold text-blue-600">£9.99</p>
                </div>
              </div>
              <a 
                href="https://checkout.revolut.com/pay/b0dd2ed2-f570-45ef-a197-55996f581ff9"
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 mb-3">
                  Pay with Revolut - £9.99
                </Button>
              </a>
              <p className="text-xs text-center text-slate-500 mb-3">🔒 Secure checkout via Revolut</p>
              <Button 
                onClick={() => navigate(`/payment-confirm?package=enrollment&student_id=${studentId}&amount=9.99`)}
                variant="outline"
                className="w-full border-green-600 text-green-600 hover:bg-green-50"
              >
                ✅ I've Paid - Confirm Payment & Continue
              </Button>
            </div>
          )}

          {/* Course Only */}
          {paymentType === 'course' && courseSelection.course_type && (
            <div className="bg-white border-2 border-purple-200 rounded-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h4 className="font-semibold text-lg">
                    {courseSelection.course_type === 'foundation' ? 'Foundation Course' : 'Pro Course'}
                  </h4>
                  <p className="text-2xl font-bold text-purple-600">
                    £{courseSelection.course_type === 'foundation' ? '75.00' : '215.00'}
                  </p>
                </div>
              </div>
              <a 
                href={courseSelection.course_type === 'foundation' 
                  ? "https://checkout.revolut.com/pay/f53257c6-373a-4c60-aea2-9e1c0f8e8589"
                  : "https://checkout.revolut.com/pay/fb2fdf10-94fc-4f52-b869-528e85913d7b"
                }
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <Button className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-4 mb-3">
                  Pay with Revolut - £{courseSelection.course_type === 'foundation' ? '75.00' : '215.00'}
                </Button>
              </a>
              <p className="text-xs text-center text-slate-500 mb-3">🔒 Secure checkout via Revolut</p>
              <Button 
                onClick={() => navigate(`/payment-confirm?package=${courseSelection.course_type}&student_id=${studentId}&amount=${courseSelection.course_type === 'foundation' ? '75.00' : '215.00'}`)}
                variant="outline"
                className="w-full border-green-600 text-green-600 hover:bg-green-50"
              >
                ✅ I've Paid - Confirm Payment & Continue
              </Button>
            </div>
          )}

          {/* Combined Package */}
          {paymentType === 'combined' && courseSelection.course_type && (
            <div className="bg-white border-2 border-emerald-200 rounded-lg p-6">
              <div className="mb-4">
                <h4 className="font-semibold text-lg">Complete Package</h4>
                <p className="text-sm text-slate-600">Enrollment + {courseSelection.course_type === 'foundation' ? 'Foundation' : 'Pro'} Course</p>
                <p className="text-2xl font-bold text-emerald-600 mt-2">
                  £{courseSelection.course_type === 'foundation' ? '84.99' : '224.99'}
                </p>
              </div>
              <a 
                href={courseSelection.course_type === 'foundation'
                  ? "https://checkout.revolut.com/pay/a6920507-b2ba-4a3a-a6a4-920d6c13b1f1"
                  : "https://checkout.revolut.com/pay/f310a48c-2631-408f-b8a2-6cadc137de76"
                }
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-4 mb-3">
                  Pay with Revolut - £{courseSelection.course_type === 'foundation' ? '84.99' : '224.99'}
                </Button>
              </a>
              <p className="text-xs text-center text-slate-500 mb-3">🔒 Secure checkout via Revolut</p>
              <Button 
                onClick={() => navigate(`/payment-confirm?package=enrollment_${courseSelection.course_type}&student_id=${studentId}&amount=${courseSelection.course_type === 'foundation' ? '84.99' : '224.99'}`)}
                variant="outline"
                className="w-full border-green-600 text-green-600 hover:bg-green-50"
              >
                ✅ I've Paid - Confirm Payment & Continue
              </Button>
            </div>
          )}
        </div>

        {/* Footer Note */}
        <div className="mt-6 text-center text-sm text-slate-600 border-t pt-4">
          <p>CPD Video Book Review Specialist Course brought to you by <strong>Voliciti Group Ventures Ltd</strong></p>
        </div>
      </div>

      <div className="flex justify-between mt-8">
        <Button onClick={prevStep} variant="outline">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>
      </div>
    </Card>
  );

  const renderStep5 = () => (
    <Card className="card max-w-2xl mx-auto text-center">
      <div className="mb-8">
        <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-slate-800">Enrollment Successful!</h2>
        <p className="text-slate-600 mt-2">Welcome to Book Review Specialists</p>
      </div>

      <Card className="p-6 bg-green-50 border-green-200 mb-6">
        <h3 className="font-semibold text-green-800 mb-4">What happens next?</h3>
        <ul className="text-left space-y-2 text-green-700">
          <li>✓ Confirmation email sent to {studentData.email}</li>
          <li>✓ Course materials will be available in your account</li>
          <li>✓ Download your bonus resources immediately below</li>
          {courseSelection.course_start_date && (
            <li>✓ Course starts on {formatDate(courseSelection.course_start_date)}</li>
          )}
          <li>✓ Join our private Facebook community</li>
        </ul>
      </Card>

      <Card className="p-6 bg-blue-50 border-blue-200 mb-6">
        <h3 className="font-semibold text-blue-800 mb-4">🎓 Complete Your Course Selection</h3>
        {paymentType === 'enrollment' ? (
          <div className="space-y-4">
            <p className="text-blue-700">
              <strong>Next Step:</strong> You can now select and pay for your preferred course level:
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-white p-4 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-slate-800">Foundation Course</h4>
                <p className="text-2xl font-bold text-blue-600">£75.00</p>
                <p className="text-sm text-slate-600 mt-2">6 comprehensive modules</p>
              </div>
              <div className="bg-white p-4 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-slate-800">Pro Course</h4>
                <p className="text-2xl font-bold text-purple-600">£215.00</p>
                <p className="text-sm text-slate-600 mt-2">11 total modules (Foundation + 5 advanced)</p>
              </div>
            </div>
            <div className="text-center">
              <Button 
                onClick={() => {
                  // Reset enrollment flow for course selection
                  setCurrentStep(2);
                  setPaymentType('course');
                }}
                className="btn btn-primary"
              >
                Select Your Course Now
              </Button>
            </div>
            <p className="text-xs text-blue-600 text-center">
              💡 You can also return later to complete your course selection
            </p>
          </div>
        ) : (
          <p className="text-blue-700">
            Your complete course package is confirmed! You'll receive access to all materials on your course start date.
          </p>
        )}
      </Card>

      {/* Instant Download Section */}
      <Card className="p-6 bg-green-50 border-green-200 mb-6">
        <h3 className="font-semibold text-green-800 mb-4">📚 Your Instant Downloads</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                📄
              </div>
              <div>
                <h4 className="font-medium text-slate-800">Ultimate Guide to Selling More Ebooks</h4>
                <p className="text-sm text-slate-600">Comprehensive report by Diane Shawe - Your enrollment bonus!</p>
              </div>
            </div>
            <a
              href="https://customer-assets.emergentagent.com/job_bookcritique-1/artifacts/5mkzo4ws_Report-on-Ultimate-Guide-to-Selling-More-ebooks_by%20Diane%20Shawe.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary text-sm px-4 py-2"
              download="Ultimate-Guide-to-Selling-More-Ebooks-by-Diane-Shawe.pdf"
            >
              Download PDF
            </a>
          </div>
          
          <div className="text-center">
            <p className="text-xs text-green-700">
              💡 <strong>Pro Tip:</strong> Save this report to your device for offline reading!
            </p>
          </div>
        </div>
      </Card>

      <div className="space-y-4">
        <Button asChild className="w-full btn-primary">
          <a href="https://www.facebook.com/groups/449766771396718/" target="_blank" rel="noopener noreferrer">
            Join Facebook Community
          </a>
        </Button>
        
        <div className="flex space-x-4">
          <Button asChild variant="outline" className="flex-1">
            <Link to="/">Return Home</Link>
          </Button>
          <Button asChild variant="outline" className="flex-1">
            <Link to="/support">Contact Support</Link>
          </Button>
        </div>
      </div>
    </Card>
  );

  // Show success flow if redirected after payment
  if (showSuccessFlow) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-25 to-indigo-50 py-8">
        {/* Full Width GIF Header */}
        <div className="w-screen relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_bookcritique-1/artifacts/csgwpycc_train%20to%20become%20a%20video%20review%20specialist.gif"
            alt="Train to Become a Video Review Specialist"
            className="w-full h-48 md:h-64 lg:h-80 object-cover object-center"
          />
          <div className="bg-white py-6">
            <div className="container mx-auto px-4 text-center">
              <h1 className="text-3xl md:text-4xl font-bold text-slate-800">
                Enrollment Complete!
              </h1>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4">
          <Card className="max-w-2xl mx-auto p-8 text-center">
            <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-slate-800 mb-4">
              🎉 Payment Successful!
            </h2>
            <p className="text-slate-600 mb-8">
              Your enrollment has been confirmed. You can now access your student dashboard to track your progress and access course materials.
            </p>

            {/* Student Login Access */}
            <div className="space-y-4">
              <Button 
                onClick={() => navigate('/student-dashboard')}
                className="w-full btn btn-primary"
                size="lg"
              >
                <User className="w-5 h-5 mr-2" />
                Access Student Dashboard
              </Button>
              
              <Button 
                onClick={() => navigate('/student-login')}
                variant="outline"
                className="w-full"
                size="lg"
              >
                <User className="w-5 h-5 mr-2" />
                Student Login (For Future Access)
              </Button>
            </div>

            <div className="mt-8 p-4 bg-blue-50 rounded-lg">
              <h3 className="font-semibold text-blue-800 mb-2">What's Next?</h3>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Access your student dashboard to track progress</li>
                <li>• Mark course stages as complete</li>
                <li>• Access your course materials when available</li>
                <li>• Bookmark the student login for future access</li>
              </ul>
            </div>

            <div className="mt-6 text-center">
              <Link to="/" className="text-blue-600 hover:text-blue-700 text-sm">
                ← Back to Homepage
              </Link>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  const renderVerificationMessage = () => (
    <Card className="card max-w-lg mx-auto text-center">
      <div className="mb-8">
        <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <Mail className="w-12 h-12 text-blue-600" />
        </div>
        <h2 className="text-2xl font-bold text-slate-800 mb-4">Check Your Email!</h2>
        <p className="text-slate-600">
          We've sent a verification link to:
        </p>
        <p className="text-lg font-semibold text-blue-600 mt-2">
          {studentData.email}
        </p>
      </div>

      <div className="bg-blue-50 rounded-xl p-6 mb-6 text-left">
        <h3 className="font-semibold text-blue-800 mb-3">Next Steps:</h3>
        <ol className="space-y-3 text-slate-700">
          <li className="flex items-start">
            <span className="bg-blue-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold mr-3 flex-shrink-0">1</span>
            <span>Open your email inbox</span>
          </li>
          <li className="flex items-start">
            <span className="bg-blue-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold mr-3 flex-shrink-0">2</span>
            <span>Click the <strong>"Verify My Email"</strong> button</span>
          </li>
          <li className="flex items-start">
            <span className="bg-blue-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold mr-3 flex-shrink-0">3</span>
            <span>Select your course and complete payment</span>
          </li>
        </ol>
      </div>

      <p className="text-sm text-slate-500 mb-6">
        Didn't receive the email? Check your spam folder or{' '}
        <button 
          onClick={async () => {
            try {
              setLoading(true);
              await axios.post(`${API}/auth/resend-verification`, { email: studentData.email });
              toast.success('Verification email resent!');
            } catch (error) {
              toast.error('Failed to resend email');
            } finally {
              setLoading(false);
            }
          }}
          className="text-blue-600 hover:underline font-medium"
          disabled={loading}
        >
          {loading ? 'Sending...' : 'click here to resend'}
        </button>
      </p>

      <Link to="/" className="text-sm text-slate-500 hover:text-slate-700">
        ← Return to Homepage
      </Link>
    </Card>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-25 to-indigo-50 py-8">
      {/* Full Width GIF Header */}
      <div className="w-screen relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] mb-8">
        <img 
          src="https://customer-assets.emergentagent.com/job_bookcritique-1/artifacts/csgwpycc_train%20to%20become%20a%20video%20review%20specialist.gif"
          alt="Train to Become a Video Review Specialist"
          className="w-full h-48 md:h-64 lg:h-80 object-cover object-center"
        />
        <div className="bg-white py-6">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-slate-800">
              Enrolment Registration
            </h1>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_bookcritique-1/artifacts/inexx1ig_IMG-20250713-WA0004%281%29.jpg"
              alt="Book Review Specialists Logo"
              className="w-6 h-6 rounded-full object-cover mr-2"
            />
            Book Review Specialists
          </Link>
          <h1 className="text-3xl font-bold text-slate-800">Course Enrollment</h1>
        </div>

        {/* Step Indicator */}
        {renderStepIndicator()}

        {/* Step Content */}
        <div className="animate-fade-in-up">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}
          {currentStep === 5 && renderStep5()}
        </div>
      </div>
    </div>
  );
};

export default EnrollmentFlow;